// Dashboard v2 — display helpers. Every money figure from /api/dashboard/* is
// integer PENCE in the store's base currency; every time is ISO-8601 UTC and is
// shown on the store's clock, Europe/London.
import { formatMoneyMajor, baseSymbol } from "../core/currency";

export const TZ = "Europe/London";

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

/** £12,345 — whole units, base currency. null → "—". */
export const money = (pence) => {
  const n = num(pence);
  return n === null ? "—" : formatMoneyMajor(n / 100, { decimals: 0 });
};

/** £12.3k / £1.25m — chart axes and dense tiles. */
export const moneyShort = (pence) => {
  const n = num(pence);
  if (n === null) return "—";
  const major = n / 100;
  const abs = Math.abs(major);
  const sign = major < 0 ? "-" : "";
  const sym = baseSymbol();
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}m`;
  if (abs >= 1e4) return `${sign}${sym}${(abs / 1e3).toFixed(1)}k`;
  return formatMoneyMajor(major, { decimals: 0 });
};

export const int = (v) => {
  const n = num(v);
  return n === null ? "—" : Math.round(n).toLocaleString("en-GB");
};

export const pct = (v, digits = 0) => {
  const n = num(v);
  return n === null ? "—" : `${n.toFixed(digits)}%`;
};

/** 52 → "52h"; 30 → "1d 6h"; null → "—". */
export const hours = (v) => {
  const n = num(v);
  if (n === null) return "—";
  if (n < 48) return `${Math.round(n)}h`;
  const d = Math.floor(n / 24);
  const h = Math.round(n - d * 24);
  return h ? `${d}d ${h}h` : `${d}d`;
};

/** Format a value by the Reports KPI `format` field. */
export const byFormat = (value, format) => {
  if (format === "pence") return moneyShort(value);
  if (format === "pct") return pct(value, 1);
  if (format === "hours") return hours(value);
  return int(value);
};

/** HH:mm on the London clock, from an ISO instant. */
export const timeLondon = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
};

/**
 * A DATE-ONLY value ("2026-10-23" or an ISO whose date part is the day). Take
 * the calendar date off the string rather than parsing the instant — a viewer
 * east of London would otherwise see the wrong day.
 */
export const dayLabel = (value, opts = { weekday: "short", day: "numeric", month: "short" }) => {
  if (!value) return "";
  const day = String(value).slice(0, 10);
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { ...opts, timeZone: "UTC" });
};

/** Today's date line, London: "Wednesday 21 October". */
export const todayLine = (now = new Date()) =>
  now.toLocaleDateString("en-GB", { timeZone: TZ, weekday: "long", day: "numeric", month: "long" });

/** The London wall-clock hour, 0–23. */
export const londonHour = (now = new Date()) =>
  Number(new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "numeric", hourCycle: "h23" }).format(now)) || 0;

export const greeting = (now = new Date()) => {
  const h = londonHour(now);
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/** Percent change, current vs previous. null when there is nothing to compare against. */
export const change = (current, previous) => {
  const c = num(current);
  const p = num(previous);
  if (c === null || p === null || p === 0) return null;
  return ((c - p) / Math.abs(p)) * 100;
};

export const initials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();

// ── Client tiers (User::segmentation) ────────────────────────────────────────
// API keys → the app's human labels. The API's own `tier_label` (round 2) wins.
export const TIER_LABELS = { top_vic: "Top VIC", vic: "VIC", potential: "Potential", core: "Core", entry: "Entry", prospect: "Prospect" };

const humanKey = (key) =>
  String(key)
    .split("_")
    .map((w) => (w.toLowerCase() === "vic" ? "VIC" : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

/**
 * "Top VIC" — plus the app's own "[Seller]" / "[Freq.Seller]" suffix when the
 * client's full segmentation label (as the client profile shows it) carries one.
 * Never the raw key ("top_vic").
 */
export const tierLabel = (client) => {
  const key = client?.tier;
  const base = client?.tier_label || TIER_LABELS[key] || (key ? humanKey(key) : "—");
  const suffix = String(client?.segmentation || "").match(/\[[^\]]+\]\s*$/);
  return suffix ? `${base} ${suffix[0].trim()}` : base;
};

/** "Last year" → "vs last year"; a month name stays capitalised ("vs September"). */
export const vsLabel = (prevLabel) => {
  if (!prevLabel) return null;
  const s = String(prevLabel);
  return `vs ${/^(Last|This|Previous|Week of)\b/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s}`;
};

// ── Celebrations ─────────────────────────────────────────────────────────────
const CELEB = { birthday: "Birthday", followup_1m: "1-month follow-up", anniversary_1y: "1 year since purchase" };
// A trailing date an older API baked into the label ("1-month follow-up Thu 22 Oct").
const TRAILING_DATE = /\s*[·,-]?\s*(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*\.?\s+)?\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?(?:\s+\d{4})?\s*$/i;

/** London calendar date "YYYY-MM-DD" of an instant. */
export const londonDate = (now = new Date()) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);

/** "1-month follow-up · Thu 22 Oct" — the label once, the date once ("today" / "tomorrow" when it is). */
export const celebrationLine = (c, now = new Date()) => {
  const label = String(c?.label || CELEB[c?.kind] || c?.kind || "").replace(TRAILING_DATE, "").trim();
  const day = String(c?.date || "").slice(0, 10);
  const today = londonDate(now);
  const tomorrow = londonDate(new Date(now.getTime() + 86400000));
  const when = !day ? "" : day === today ? "today" : day === tomorrow ? "tomorrow" : dayLabel(day);
  return [label, when].filter(Boolean).join(" · ");
};
